/**
 * Timeline panel — chronological feed of every notable event on this
 * incident. Every SitRep version, every IAP regen, every meeting event,
 * every pre-meeting refresh email, every close. The single source of
 * truth for a post-incident debrief.
 *
 * Backed by GET /cima/incidents/:id/timeline (commit dad2d46).
 */
import React, { useEffect, useState } from 'react';
import { TimelineEvent, listTimeline } from '../api/timeline';

const EVENT_META: Record<string, { icon: string; title: string; colour: string }> = {
  sitrep_version_saved:    { icon: '', title: 'SitRep updated',            colour: '#1e40af' },
  iap_version_saved:       { icon: '', title: 'IAP regenerated',           colour: '#0d9488' },
  iap_version_signed:      { icon: '',  title: 'IAP signed off',            colour: '#0d9488' },
  meeting_scheduled:       { icon: '', title: 'CIMT meeting scheduled',    colour: '#0891b2' },
  meeting_notes_pulled:    { icon: '', title: 'Meeting notes pulled',      colour: '#16a34a' },
  pre_meeting_refresh_sent:{ icon: '', title: 'Invitees re-notified',      colour: '#f59e0b' },
  incident_closed:         { icon: '', title: 'Incident closed',           colour: '#6b7280' },
  incident_merged_in:      { icon: '', title: 'Duplicate merged in',       colour: '#0891b2' },
  incident_merged_into:    { icon: '', title: 'Merged into another',       colour: '#6b7280' },
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', dateStyle: 'medium', timeStyle: 'short' });
}

function describe(e: TimelineEvent): string {
  const meta = EVENT_META[e.eventType] || { title: e.eventType };
  const p = e.payload || {};
  switch (e.eventType) {
    case 'sitrep_version_saved':
      return `${p.majorBump ? 'Major ' : ''}v${p.version}${p.triggeredBy ? ` · ${p.triggeredBy}` : ''}`;
    case 'iap_version_saved':
      return `${p.majorBump ? 'Major ' : ''}v${p.version}${p.triggeredBy ? ` · ${p.triggeredBy}` : ''}`;
    case 'meeting_scheduled':
      return `${p.title || 'Meeting'}${p.pinnedSitrepVersionId ? ` · pinned SitRep v-id ${p.pinnedSitrepVersionId}` : ''}`;
    case 'meeting_notes_pulled':
      return `Meeting ${p.meetingId ?? ''} · source: ${p.source || '?'} · ${p.noteChars || 0} chars`;
    case 'pre_meeting_refresh_sent':
      return `Updated XLSX notice sent · SitRep now v${p.latestSitrepLabel || '?'} · ${p.recipients || 0} recipient(s)`;
    case 'incident_closed':
      return 'Frozen as final record';
    case 'iap_version_signed':
      return `IAP v${p.version} signed`;
    case 'incident_merged_in':
      return `Pulled in ${p.sourceIncidentId}`;
    case 'incident_merged_into':
      return `Closed as duplicate of ${p.primaryIncidentId}`;
    default:
      return JSON.stringify(p).slice(0, 120);
  }
}

export function TimelinePanel({ incidentId }: { incidentId: string }) {
  const [events, setEvents]   = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  async function reload() {
    setLoading(true);
    try {
      const list = await listTimeline(incidentId);
      // newest first
      setEvents(list.reverse());
      setError('');
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, [incidentId]);

  const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
  return (
    <div style={{ padding: '18px 20px' }}>
      {/* Record accent — matches the SitRep / IAP / CIMT / Meeting stages. */}
      <div style={{ height: 3, borderRadius: 2, marginBottom: 16, background: 'var(--primary)' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 22 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--fg)', lineHeight: 1.15 }}>Timeline</div>
          <div style={{ fontFamily: mono, fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            {events.length} event{events.length === 1 ? '' : 's'} · newest first
          </div>
        </div>
        <button
          onClick={reload}
          title="Refresh"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
            background: 'var(--primary-dim)', border: '1px solid var(--primary-border)',
            borderRadius: 8, color: 'var(--primary)',
            padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
          Refresh
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 2px' }}>Loading…</p>
      ) : error ? (
        <p style={{ color: 'var(--accent-red)', fontSize: 13 }}>⚠ {error}</p>
      ) : events.length === 0 ? (
        <div style={{
          border: '1px dashed var(--border-md)', borderRadius: 12,
          padding: '30px 18px', textAlign: 'center', color: 'var(--muted)', fontSize: 13, lineHeight: 1.5,
        }}>
          No events yet. Once the SitRep is saved, meetings scheduled, notes pulled, and versions bumped, they&rsquo;ll all show up here.
        </div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          {/* vertical rail */}
          <div style={{
            position: 'absolute', left: 9, top: 8, bottom: 8,
            width: 2, background: 'color-mix(in srgb, var(--primary) 22%, var(--border))', borderRadius: 1,
          }} />
          {events.map((e) => {
            const meta = EVENT_META[e.eventType] || { icon: '', title: e.eventType, colour: 'var(--primary)' };
            return (
              <div key={e.id} style={{ position: 'relative', marginBottom: 12 }}>
                {/* marker */}
                <div style={{
                  position: 'absolute', left: -24, top: 4,
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'var(--card)',
                  border: `2px solid ${meta.colour}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.colour }} />
                </div>
                <div style={{
                  background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 12, padding: '12px 14px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--fg)' }}>
                      {meta.title}
                    </span>
                    <span style={{ fontFamily: mono, fontSize: 11, color: 'var(--subtle)', whiteSpace: 'nowrap' }}>
                      {formatWhen(e.occurredAt)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
                    {describe(e)}
                    {e.actor && (
                      <span style={{ marginLeft: 8, color: 'var(--subtle)', fontStyle: 'italic' }}>
                        — {e.actor}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
